/**
 * TasksQuickHeaderTimer - Cápsula de execução junto ao avatar do usuário.
 *
 * Exibe a soma das tarefas em andamento e o tempo ativo da tarefa iniciada há
 * mais tempo. O clique abre o painel compartilhado, sem interferir no perfil.
 *
 * @version 1.2.0
 * RELEASE: 2026-10-04 v1.2.0 - Remove divisórias e reforça a integração por sombra do conjunto.
 * RELEASE: 2026-10-04 v1.1.0 - Integra visualmente ao avatar e mantém acesso sem execução.
 * RELEASE: 2026-10-04 v1.0.0 - Criação inspirada no indicador do Bitrix24.
 */
import { useEffect, useState } from 'react';
import { Timer } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTasksQuick } from '@/contexts/TasksQuickContext';
import { calculateTaskActiveSeconds, formatTaskTimer } from '@/lib/tasks/taskTimer';

export function TasksQuickHeaderTimer() {
  const quickTasks = useTasksQuick();
  const [now, setNow] = useState(Date.now());
  const task = quickTasks?.runningTask ?? null;

  // Atualiza somente enquanto existe uma tarefa realmente em execução.
  useEffect(() => {
    if (!task) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [task?.id]);

  if (!quickTasks?.visible) return null;

  const activeSeconds = task
    ? calculateTaskActiveSeconds(
      task.started_at,
      task.is_paused,
      task.total_pause_duration_seconds,
      task.current_pause_started_at,
      task.completed_at,
      task.cancelled_at,
      now,
    )
    : 0;
  const timerLabel = task ? formatTaskTimer(activeSeconds) : 'Sem execução';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          onClick={quickTasks.openSheet}
          aria-label={`${quickTasks.runningCount} tarefas em andamento. ${task ? `Tempo da tarefa destacada: ${timerLabel}` : timerLabel}`}
          className="h-9 min-w-[104px] gap-2 rounded-none rounded-l-full border-0 bg-primary/10 pl-2.5 pr-3 text-primary shadow-none transition-colors duration-200 hover:bg-primary/20 hover:text-primary"
        >
          <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">
            {quickTasks.runningCount}
          </span>
          <Timer className="hidden h-3.5 w-3.5 shrink-0 sm:block" />
          <span className="min-w-[58px] text-xs font-semibold tabular-nums">
            {timerLabel}
          </span>
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Abrir minhas tarefas em andamento</TooltipContent>
    </Tooltip>
  );
}